/*
 *
 * Artifactory is a binaries repository manager.
 * Copyright (C) 2018 JFrog Ltd.
 *
 * Artifactory is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 * Artifactory is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Artifactory.  If not, see <http://www.gnu.org/licenses/>.
 *
 */
import { artifactoryNotificationsInterceptor } from './artifactory_notifications_interceptor.js';
import { ArtifactoryDao, ArtifactoryDaoFactory } from './artifactory_dao.js';
import { AdminSecurityGeneralDao } from './dao/admin_security_general_dao';
import { DistributableSearchDao } from './dao/distributable_search_dao';
import { GlobalSearchDao } from './dao/global_search_dao';
import { GroupsDao } from './dao/groups_dao';
import { GroupPermissionsDao } from './dao/group_permissions_dao';
import { PipelinesPermissionsDao } from './dao/pipelines_effective_permissions_dao';
import { HttpSsoDao } from './dao/http_sso_dao';
import { SshServerDao } from './dao/ssh_server_dao';
import { LicensesDao } from './dao/licenses_dao';
import { MailDao } from './dao/mail_dao';
import { PasswordsEncryptionDao } from './dao/passwords_encryption_dao';
import { ProxiesDao } from './dao/proxies_dao';
import { ReverseProxiesDao } from './dao/reverse_proxies_dao';
import { RegisterProDao } from './dao/register_pro_dao';
import { UserDao } from './dao/user_dao';
import { SamlDao } from './dao/saml_dao';
import { BintrayDao } from './dao/bintray_dao';
import { PropertyFactory, PropertySetFactory, PropertySetsDao } from './dao/property_sets_dao';
import { BackupDao } from './dao/backup_dao';
import { BrowseFilesDao } from './dao/browse_files_dao';
import { SystemInfoDao } from './dao/system_info_dao';
import { SecurityDescriptorDao } from './dao/security_descriptor_dao';
import { ConfigDescriptorDao } from './dao/config_descriptor_dao';
import { IndexerDao } from './dao/indexer_dao';
import { StorageSummaryDao } from './dao/storage_summary_dao';
import { TreeBrowserDao } from './dao/tree_browser_dao';
import { TreeNodeFactory } from './dao/tree_node';
import { RepoDataDao } from './dao/repo_data_dao';
import { CronTimeDao } from './dao/cron_time_dao';
import { DateFormatDao } from './dao/date_format_dao';
import { NameValidatorDao } from './dao/name_validator_dao';
import { UniqueIdValidatorDao } from './dao/unique_id_validator_dao';
import { XmlNameDao } from './dao/xml_name_dao';
import { ArtifactGeneralDao } from './dao/artifact/artifact_general_dao';
import { ArtifactPermissionsDao } from './dao/artifact/artifact_permissions_dao';
import { ArtifactBuildsDao } from './dao/artifact/artifact_builds_dao';
import { ArtifactViewSourceDao } from './dao/artifact/artifact_viewsource_dao';
import { ArtifactPropertyDao } from './dao/artifact/artifact_property_dao';
import { ArtifactWatchesDao } from './dao/artifact/artifact_watches_dao';
import { ArtifactXrayDao } from './dao/artifact/artifact_xray_dao';
import { XrayDataDao } from './dao/artifact/xray_data_dao';
import { ArtifactActionsDao } from './dao/artifact/artifact_actions_dao';
import { PredefineDao } from './dao/predefine_values_dao';
import { RepoPropertySetDao } from './dao/repo_property_set_dao';
import { ArtifactSearchDao } from './dao/artifact/artifact_search_dao';
import { ArtifactPackageSearchDao } from './dao/artifact/artifact_package_search_dao';
import { DependencyDeclarationDao } from './dao/dependency_declaration_dao';
import { ArtifactDeployDao } from './dao/artifact/artifact_deploy_dao';
import { ArtifactViewsDao } from './dao/artifact/artifact_views_dao';
import { FooterDao } from './dao/footer_dao';
import { ExportDao } from './dao/export_dao';
import { ImportDao } from './dao/import_dao';
import { MaintenanceDao } from './dao/maintenance_dao';
import { LdapDao } from './dao/ldap_dao';
import { DockerStatusDao } from './dao/docker_status_dao';
import { LdapGroupsDao } from './dao/ldap_groups_dao';
import { PushToBintrayDao } from './dao/push_to_bintray_dao';
import { DistributionDao } from './dao/distribution_dao';
import { GeneralConfigDao } from './dao/general_config_dao';
import { TrustedKeysDao } from './dao/trusted_keys_dao';
import { AccessTokensDao } from './dao/access_tokens_dao';
import { KeystoreDao } from './dao/keystore_dao';
import { BuildsDao } from './dao/builds/builds_dao';
import { BundlesDao } from './dao/bundles_dao';
import { BundlesEffectivePermissionDao } from './dao/bundles_effective_permissions_dao';
import { UserProfileDao } from './dao/user_profile_dao';
import { PermissionsDao } from './dao/permissions_dao';
import { ChecksumsDao } from './dao/checksums_dao';
import { FilteredResourceDao } from './dao/filtered_resource_dao';
import { ArtifactLicensesDao } from './dao/artifact/artifact_licenses_dao';
import { HomePageDao } from './dao/home_page_dao';
import { SetMeUpDao } from './dao/set_me_up_dao';
import { SystemLogsDao } from './dao/system_logs_dao';
import { RepositoriesDao } from './dao/repositories_dao';
import { RepositoriesLayoutsDao } from './dao/repositories_layouts_dao';
import { HaDao } from './dao/ha_dao';
import { HaLicensesDao } from './dao/ha_licenses_dao';
import { StashResultsDao } from './dao/stash_results_dao';
import { OAuthDao } from './dao/oauth_dao';
import { SupportPageDao } from './dao/support_page_dao';
import {StorageSummaryCachedDao} from "./dao/storage_summary_cached_dao";
import { ServerTimeDao } from './dao/server_time_dao';
import { SshClientDao } from './dao/ssh_client_dao';
import { BinaryProvidersInfoDao } from './dao/binary_providers_info_dao';
import { XrayDao } from './dao/xray_dao';
import { GlobalReplicationsConfigDao } from './dao/global_replications_config_dao';
import { SumoLogicConfigDao } from './dao/sumologic_config_dao';
import { OnboardingDao } from './dao/onboarding_dao';
import { SslCertificateDao } from './dao/ssl_certificates_dao';
import { PackagesDAO } from './dao/packages_dao';
import { ProjectAdminDao } from './dao/project_admin_dao';

angular.module('artifactory.dao', ['ngResource', 'artifactory.services', 'jfrog.ui.essentials'])
    .factory('artifactoryNotificationsInterceptor', artifactoryNotificationsInterceptor)
    .service('ArtifactoryDao', ArtifactoryDao)
    .factory('ArtifactoryDaoFactory', ArtifactoryDaoFactory)
    .service('AdminSecurityGeneralDao', AdminSecurityGeneralDao)
    .service('GroupsDao', GroupsDao)
    .service('GroupPermissionsDao', GroupPermissionsDao)
    .service('HttpSsoDao', HttpSsoDao)
    .service('SshServerDao', SshServerDao)
    .service('LicensesDao', LicensesDao)
    .service('HaLicensesDao', HaLicensesDao)
    .service('MailDao', MailDao)
    .service('PasswordsEncryptionDao', PasswordsEncryptionDao)
    .factory('ProxiesDao', ProxiesDao)
    .factory('ReverseProxiesDao', ReverseProxiesDao)
    .service('RegisterProDao', RegisterProDao)
    .service('UserDao', UserDao)
    .service('SamlDao', SamlDao)
    .service('BintrayDao', BintrayDao)
    .service('GlobalSearchDao', GlobalSearchDao)
    .service('DistributableSearchDao', DistributableSearchDao)
    .factory('PropertySetsDao', PropertySetsDao)
    .factory('Property', PropertyFactory)
    .factory('PropertySet', PropertySetFactory)
    .factory('BackupDao', BackupDao)
    .service('BrowseFilesDao', BrowseFilesDao)
    .service('SystemInfoDao', SystemInfoDao)
    .service('SecurityDescriptorDao', SecurityDescriptorDao)
    .service('ConfigDescriptorDao', ConfigDescriptorDao)
    .service('IndexerDao', IndexerDao)
    .service('StorageSummaryDao', StorageSummaryDao)
    .service('TreeBrowserDao', TreeBrowserDao)
    .factory('TreeNode', TreeNodeFactory)
    .service('RepoDataDao', RepoDataDao)
    .service('CronTimeDao', CronTimeDao)
    .service('DateFormatDao', DateFormatDao)
    .service('NameValidatorDao', NameValidatorDao)
    .service('UniqueIdValidatorDao', UniqueIdValidatorDao)
    .service('XmlNameDao', XmlNameDao)
    .service('ArtifactGeneralDao', ArtifactGeneralDao)
    .service('ArtifactPermissionsDao', ArtifactPermissionsDao)
    .service('ArtifactBuildsDao', ArtifactBuildsDao)
    .service('ArtifactViewSourceDao', ArtifactViewSourceDao)
    .service('ArtifactPropertyDao', ArtifactPropertyDao)
    .factory('ArtifactWatchesDao', ArtifactWatchesDao)
    .factory('ArtifactXrayDao', ArtifactXrayDao)
    .factory('XrayDataDao', XrayDataDao)
    .factory('ArtifactActionsDao', ArtifactActionsDao)
    .service('PredefineDao', PredefineDao)
    .service('RepoPropertySetDao', RepoPropertySetDao)
    .service('ArtifactSearchDao', ArtifactSearchDao)
    .service('ArtifactPackageSearchDao', ArtifactPackageSearchDao)
    .service('DependencyDeclarationDao', DependencyDeclarationDao)
    .service('ArtifactDeployDao', ArtifactDeployDao)
    .service('ArtifactViewsDao', ArtifactViewsDao)
    .service('FooterDao', FooterDao)
    .service('ExportDao', ExportDao)
    .service('ImportDao', ImportDao)
    .service('MaintenanceDao', MaintenanceDao)
    .service('LdapDao', LdapDao)
    .service('LdapGroupsDao', LdapGroupsDao)
    .service('PushToBintrayDao', PushToBintrayDao)
    .service('DistributionDao', DistributionDao)
    .service('GeneralConfigDao', GeneralConfigDao)
    .service('TrustedKeysDao', TrustedKeysDao)
    .service('AccessTokensDao', AccessTokensDao)
    .service('KeystoreDao', KeystoreDao)
    .service('BuildsDao', BuildsDao)
    .service('BundlesDao', BundlesDao)
    .service('BundlesEffectivePermissionDao', BundlesEffectivePermissionDao)
    .service('UserProfileDao', UserProfileDao)
    .service('PermissionsDao', PermissionsDao)
    .service('ChecksumsDao', ChecksumsDao)
    .service('FilteredResourceDao', FilteredResourceDao)
    .service('ArtifactLicensesDao', ArtifactLicensesDao)
    .service('HomePageDao', HomePageDao)
    .service('SetMeUpDao', SetMeUpDao)
    .service('SystemLogsDao', SystemLogsDao)
    .service('RepositoriesDao', RepositoriesDao)
    .service('DockerStatusDao', DockerStatusDao)
    .service('RepositoriesLayoutsDao', RepositoriesLayoutsDao)
    .service('StashResultsDao', StashResultsDao)
    .service('OAuthDao', OAuthDao)
    .service('SupportPageDao', SupportPageDao)
    .service('ServerTimeDao', ServerTimeDao)
    .service('StorageSummaryCachedDao', StorageSummaryCachedDao)
    .service('SshClientDao', SshClientDao)
    .service('BinaryProvidersInfoDao', BinaryProvidersInfoDao)
    .service('GlobalReplicationsConfigDao', GlobalReplicationsConfigDao)
    .service('XrayDao', XrayDao)
    .service('SumoLogicConfigDao', SumoLogicConfigDao)
    .service('OnboardingDao', OnboardingDao)
    .service('SslCertificateDao', SslCertificateDao)
    .service('PackagesDAO', PackagesDAO)
    .service('PipelinesPermissionsDao', PipelinesPermissionsDao)
    .service('ProjectAdminDao', ProjectAdminDao)
    .factory('HaDao', HaDao);



// WEBPACK FOOTER //
// ./data/artifactory_dao_module.js